import { Briefcase, Globe, FileText, Share2, Download, Check, ArrowRight, Zap, Home, Plane, BookOpen, Shield, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { T, LogoMark, Wordmark } from '../theme';
import { setPageMeta } from '../utils/pageMeta';
import GlobalFooterBar from './GlobalFooterBar';

const DISCIPLINES = [
  'Strategy', 'IT & Tech', 'Finance', 'Marketing', 'HR & Talent',
  'Legal', 'Operations', 'Design', 'Management', 'Data & Analytics',
];

const EXPENSE_TYPES = [
  {
    icon: Plane,
    label: 'Travel & Lodging',
    tip: 'Flights, hotels, taxis, and per diem meals — captured per client engagement',
  },
  {
    icon: Briefcase,
    label: 'Client Meetings',
    tip: 'Dining, entertainment, and venue costs tied to specific clients or projects',
  },
  {
    icon: Home,
    label: 'Home Office',
    tip: 'Internet, phone, equipment, and supplies used for your consulting practice',
  },
  {
    icon: BookOpen,
    label: 'Professional Development',
    tip: 'Courses, certifications, conferences, and trade publications',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'One report per client',
    desc: 'Create a named expense report for each engagement. Switch between clients instantly — expenses never get mixed up.',
  },
  {
    num: '2',
    title: 'Capture on the go',
    desc: 'Scan receipts the moment they happen — at the airport, client site, or hotel. AI fills in vendor, amount, and category automatically. You can always move expenses to another report later, so just scan it while you still have the receipt.',
  },
  {
    num: '3',
    title: 'Share or export',
    desc: 'Send a live review link to your client for reimbursement approval, or export to PDF, Excel, or CSV for your accountant. Optionally, include images of all receipts nicely formatted and cross-referenced to each expense.  No more taping loose receipts onto blank pages for submittal.',
  },
];

const TESTIMONIALS = [
  {
    quote: "I bill five clients at once. Before MySpend, I'd spend hours at month-end sorting receipts into folders. Now I just open the right report and export.",
    name: 'David K.',
    role: 'Independent strategy consultant',
  },
  {
    quote: "My clients expect a clean expense summary before they'll reimburse anything. The PDF export looks professional and takes 30 seconds to generate.",
    name: 'Anika R.',
    role: 'Freelance UX consultant',
  },
];

function GetStartedButton({ onSignUp, label = "Get Started — It's Free", size = 'lg' }) {
  const lg = size === 'lg';
  return (
    <button
      onClick={onSignUp}
      style={{
        padding: lg ? '15px 32px' : '12px 24px',
        borderRadius: 14, border: 'none',
        background: T.gradTeal, color: '#fff',
        fontSize: lg ? 16 : 14, fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      {label} <ArrowRight size={lg ? 18 : 15} />
    </button>
  );
}

function ConsultantLandingPage({ onSignUp, onSignIn, onSkip, onPricing }) {
  useEffect(() => {
    setPageMeta({
      title: 'Expense Tracking for Independent Consultants',
      description: 'Separate expenses by client, send live review links for approval, and get reimbursed faster. The expense tracker built for freelance consultants.',
      url: 'https://myspend.info/?page=consultant',
    });
  }, []);
  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh', paddingBottom: 36 }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <Wordmark size={18} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onPricing} style={{
          background: 'none', border: 'none',
          color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
          padding: '8px 12px', cursor: 'pointer',
        }}>
          Pricing
        </button>
          <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>
            Sign In
          </button>
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(5,196,176,0.3)',
          }}>
            Get Started Free
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '72px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>
              FOR INDEPENDENT CONSULTANTS
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(30px, 6vw, 52px)', fontWeight: 900,
            color: '#fff', margin: '0 0 20px', lineHeight: 1.15, letterSpacing: -1,
          }}>
            Client expenses, organized.<br />
            <span style={{ color: T.tealBright }}>Reimbursed faster.</span>
          </h1>

          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.65)', margin: '0 0 16px',
            lineHeight: 1.65, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Track expenses per client, scan receipts on the road, and send
            a professional report in minutes. Built for consultants who bill multiple clients at once.
          </p>

          <p style={{ fontSize: 15, fontWeight: 800, color: T.tealBright, margin: '0 0 36px', letterSpacing: 0.3 }}>
            Free to use. No credit card. No commitment.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
            <button onClick={onSignIn} style={{
              padding: '15px 32px', borderRadius: 14,
              border: '1.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>

        </div>
      </section>

      {/* ── Discipline strip ─────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
          Used across consulting disciplines
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', justifyContent: 'center' }}>
          {DISCIPLINES.map(d => (
            <span key={d} style={{ fontSize: 13, fontWeight: 700, color: '#374151', opacity: 0.7 }}>{d}</span>
          ))}
        </div>
      </div>

      {/* ── Problem hook ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
          Mixing client expenses is a <span style={{ color: T.tealDeep }}>billing nightmare</span>
        </h2>
        <p style={{ fontSize: 16, color: '#6B7280', lineHeight: 1.65, maxWidth: 520, margin: '0 auto 40px' }}>
          When you work with multiple clients simultaneously, expenses blur together.
          MySpend.info keeps each client's expenses in a separate named report —
          so reimbursements are accurate, tax time is clean, and clients see exactly what they owe.  And if you do add an expense to the wrong 
          report, moving or copying it is super-simple. So just add it when you can, you can always move it around as needed.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['Per-client reports', 'Share for approval', 'Multi-currency', 'Tax-ready export'].map(item => (
            <div key={item} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: T.tealPale, borderRadius: 20, padding: '6px 14px',
            }}>
              <Check size={13} color={T.tealDeep} strokeWidth={3} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.tealDeep }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Expense types ────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 8px' }}>
            Every consultant expense, covered
          </h2>
          <p style={{ fontSize: 15, color: '#9CA3AF', textAlign: 'center', marginBottom: 40 }}>
            Built-in categories map to IRS Schedule C deductions — so your accountant gets what they need.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {EXPENSE_TYPES.map(({ icon: Icon, label, tip }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                background: T.appBg, borderRadius: 14, padding: '18px 20px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={T.tealDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.navy, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 48px' }}>
          From receipt to reimbursement in three steps
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 22, flexShrink: 0,
                background: T.gradTeal, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 900, color: '#fff',
                boxShadow: '0 4px 14px rgba(5,196,176,0.35)',
              }}>
                {step.num}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.navy, marginBottom: 6 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Sharing callout ──────────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: 'rgba(5,196,176,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', border: '1px solid rgba(5,196,176,0.3)',
          }}>
            <Share2 size={30} color={T.tealBright} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: '#fff', margin: '0 0 16px' }}>
            Clients approve in one click
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 16 }}>
            Generate a secure review link and send it to your client.
            They see a live, read-only expense report — no account required on their end.
            They can leave comments, you get paid faster.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', marginBottom: 32 }}>
            Or export to PDF, Excel, or CSV if your client prefers a document.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
        </div>
      </section>

      {/* ── AI Bookkeeper callout ───────────────────────────────────── */}
      <section style={{ background: T.tealPale, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Sparkles size={30} color={T.tealDeep} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
            Instant answers about any client's numbers
          </h2>
          <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.65, marginBottom: 28 }}>
            The AI Bookkeeper knows your data. Ask it to summarise any client, project, or date range in plain English — no digging through rows or building reports manually.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', marginBottom: 32 }}>
            {[
              '"Summarise all expenses for Client Acme in Q2"',
              '"What\'s my total billable travel spend this year?"',
              '"Show a chart of expenses by client for March"',
              '"Which engagement had the highest total expenses?"',
            ].map(q => (
              <div key={q} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={T.tealDeep} />
                </div>
                <span style={{ fontSize: 14, color: '#374151', fontStyle: 'italic' }}>{q}</span>
              </div>
            ))}
          </div>
          <GetStartedButton onSignUp={onSignUp} label="Try It Free" />
        </div>
      </section>

      {/* ── Multi-currency callout ───────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 48, flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20, background: T.tealPale,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <Globe size={30} color={T.tealDeep} />
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 900, color: T.navy, margin: '0 0 12px' }}>
              International clients? No problem.
            </h2>
            <p style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.65, margin: '0 0 20px' }}>
              Track expenses in the local currency of wherever you're working.
              Live exchange rates convert everything to your report currency automatically —
              with an optional adjustment factor for bank spread or company policy.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['100+ currencies', 'Live exchange rates', 'Adjustable markup'].map(item => (
                <div key={item} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: T.tealPale, borderRadius: 20, padding: '5px 12px',
                }}>
                  <Check size={12} color={T.tealDeep} strokeWidth={3} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.tealDeep }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section style={{ background: T.appBg, padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 40px' }}>
            What consultants are saying
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 16, padding: '24px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{ fontSize: 28, color: T.tealBright, fontWeight: 900, lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65, margin: '0 0 16px', fontStyle: 'italic' }}>
                  {t.quote}
                </p>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Export callout ───────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 48, flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 900, color: T.navy, margin: '0 0 12px' }}>
              Tax time in minutes, not days
            </h2>
            <p style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.65, margin: '0 0 20px' }}>
              All your reports, all year long. When your accountant asks for Schedule C expenses,
              export a tax report that maps every category to an IRS deduction.
              No spreadsheet archaeology required.
            </p>
            <GetStartedButton onSignUp={onSignUp} label="Get Started Now" size="sm" />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20, background: T.amberPale,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
            }}>
              <Download size={30} color={T.amberDeep} />
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>PDF · Excel · CSV · Tax Report</div>
          </div>
        </div>
      </section>

      {/* ── Privacy strip ────────────────────────────────────────────── */}
      <div style={{ background: '#F0FDF4', borderTop: '1px solid #BBF7D0', borderBottom: '1px solid #BBF7D0', padding: '20px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={17} color="#15803d" />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#15803d' }}>Client data stays confidential</span>
          </div>
          {[
            'Import bank & CC statements locally — never uploaded',
            'AES-256 encrypted on your device',
            'No selling, no sharing, ever',
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={13} color="#15803d" strokeWidth={3} />
              <span style={{ fontSize: 13, color: '#374151' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', textAlign: 'center', background: T.gradNavy }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, color: '#fff', margin: '0 0 16px', lineHeight: 1.2 }}>
            Focus on the work.<br />
            <span style={{ color: T.tealBright }}>Let expenses take care of themselves.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
            Free forever for basic tracking. No credit card required.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now — It's Free" />
        </div>
      </section>

      <GlobalFooterBar />

    </div>
  );
}

export default ConsultantLandingPage;
